<template>
  <div
    v-if="open"
    class="ds-modal"
    :class="{ 'ds-modal--full-screen': fullScreen }"
  >
    <div class="ds-modal__overlay" @click="onClickOutside"></div>
    <div class="ds-modal__wrapper">
      <div class="ds-modal__component" :style="{ width, height }">
        <div class="ds-modal__content">
          <div class="ds-modal__header">
            <div class="ds-modal__title">
              {{ title }}
            </div>

            <div v-if="subtitle" class="ds-modal__subtitle">
              {{ subtitle }}
            </div>
          </div>

          <div
            v-if="text || areThereComponentToRender"
            class="ds-modal__container"
          >
            <div v-if="text" class="ds-modal__text">
              {{ text }}
            </div>

            <div
              v-if="areThereComponentToRender"
              class="ds-modal__slot-content"
            >
              <!-- @slot use this slot to insert another component -->
              <slot name="content"></slot>
            </div>
          </div>
        </div>

        <div class="ds-modal__footer">
          <a
            v-if="link && linkLabel"
            :href="link"
            target="_blank"
            class="ds-modal__link"
          >
            {{ linkLabel }}
          </a>

          <button
            v-if="!link && linkLabel"
            :class="linkButtonClasses"
            @click="onClickLinkButton()"
            type="button"
          >
            {{ linkLabel }}
          </button>

          <div class="ds-modal__buttons">
            <StButton
              v-if="secondaryButtonLabel"
              color="secondary"
              :dark="dark"
              @click="onClickSecondaryButton()"
            >
              {{ secondaryButtonLabel }}
            </StButton>

            <StButton
              v-if="primaryButtonLabel"
              color="primary"
              :dark="dark"
              @click="onClickPrimaryButton()"
            >
              {{ primaryButtonLabel }}
            </StButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import Vue from 'vue';
import StButton from '../Button/Button.vue';

export default Vue.extend({
  name: 'DsModal',
  components: {
    StButton
  },
  props: {
    /**
     * controls the opening and closing of the modal
     */
    open: {
      type: Boolean,
      default: false
    },
    /**
     * modal title
     */
    title: {
      type: String,
      default: '',
      require: true
    },
    /**
     * modal subtitle
     */
    subtitle: {
      type: String,
      default: ''
    },
    /**
     * description of what the modal wants to deal with
     */
    text: {
      type: String,
      default: ''
    },
    /**
     * link used to direct the user
     */
    link: {
      type: String,
      default: ''
    },
    /**
     * label used on the link
     */
    linkLabel: {
      type: String,
      default: ''
    },
    /**
     * primary button label
     */
    primaryButtonLabel: {
      type: String,
      default: ''
    },
    /**
     * secondary button label
     */
    secondaryButtonLabel: {
      type: String,
      default: '',
      require: true
    },
    /**
     * modal width
     */
    width: {
      type: String,
      default: '980px'
    },
    /**
     * modal height
     */
    height: {
      type: String,
      required: false
    },
    /**
     * boolean that controls the component color between light and dark theme
     */
    dark: {
      type: Boolean,
      default: false
    },
    /**
     * boolean that controls whether the modal will be persistent
     */
    persistent: {
      type: Boolean,
      default: true
    },
    /**
     * boolean that able the modal to be full screen
     */
    fullScreen: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    areThereComponentToRender() {
      if (this.$slots.content === undefined) {
        return false;
      }

      return this.$slots.content.length;
    },
    linkButtonClasses() {
      return ['ds-modal__link', 'ds-modal__link--button'];
    }
  },
  mounted() {
    document.documentElement.setAttribute(
      'data-color-theme',
      this.dark ? 'dark' : ''
    );
  },
  methods: {
    onClickPrimaryButton() {
      /**
       * event emitted when clicking the primary button
       */
      this.$emit('primary');
    },
    onClickSecondaryButton() {
      /**
       * event emitted when clicking the secondary button
       */
      this.$emit('secondary');
    },
    onClickOutside() {
      if (!this.persistent) {
        /**
         * when the modal is not persistent, clicking away emits this event
         */
        this.$emit('secondary');
      }
    },
    onClickLinkButton() {
      /**
       * when passed only 'link-Label' property, this event emitted when clicking on link
       */
      this.$emit('link');
    }
  }
});
</script>

<style lang="scss">
@import '~@ds/styles/Modal.scss';
</style>
